import { MetricName } from '@canvas/frontend-metrics';

import { defineMetric, type MetricMetadataBase } from '../define-metric.ts';
import type { FrameState } from '../stabilized-scheduler.ts';

import {
  isElementVisiblyRendered,
  rectsIntersectWithPositiveArea,
  SELECTIONS_OVERLAY_CONTAINER_ID
} from './selection-dom.ts';

const HIGHLIGHT_SELECTION_CLASS_NAME = 'highlight-selection';

const SCROLLABLE_INNER_CONTAINER_ID = 'scrollable-inner-container';

const RANGE_HIGHLIGHT_SELECTOR =
  `#${SELECTIONS_OVERLAY_CONTAINER_ID} .${HIGHLIGHT_SELECTION_CLASS_NAME}, ` +
  `[data-highlight-surface="pinned-leading"] .${HIGHLIGHT_SELECTION_CLASS_NAME}`;

interface CellRangeSelectionPointerDragCommitProbeData {
  tableRoot: HTMLElement | null;
  rowIndex: number;
  colIndex: number;
  entryId: string;
  fieldId: string;
}

/**
 * Defines pointer-drag commit latency as the final `extendToCell` dispatch through
 * a stable range highlight over the release cell. Preview rendering and human drag
 * duration are outside this interval.
 */
function getRangeHighlightVisibleForTargetCellFrameState(
  tableRoot: HTMLElement | null,
  target: Omit<CellRangeSelectionPointerDragCommitProbeData, 'tableRoot'>
): FrameState {
  if (!tableRoot) {
    return { done: false, stabilityKey: null };
  }

  const { rowIndex, colIndex, entryId, fieldId } = target;
  const targetCell = tableRoot.querySelector<HTMLElement>(
    `[data-row-index="${rowIndex}"][data-col-index="${colIndex}"]`
  );
  const scrollContainer = tableRoot.closest<HTMLElement>(
    `#${SCROLLABLE_INNER_CONTAINER_ID}`
  );

  if (!targetCell || !scrollContainer) {
    return { done: false, stabilityKey: null };
  }

  const targetRect = targetCell.getBoundingClientRect();
  const selectionHighlights = scrollContainer.querySelectorAll<HTMLElement>(
    RANGE_HIGHLIGHT_SELECTOR
  );

  for (const highlight of selectionHighlights) {
    if (
      isElementVisiblyRendered(highlight) &&
      rectsIntersectWithPositiveArea(highlight.getBoundingClientRect(), targetRect)
    ) {
      return {
        done: true,
        stabilityKey: `${entryId}:${fieldId}:range-highlight`
      };
    }
  }

  return { done: false, stabilityKey: null };
}

export const cellRangeSelectionPointerDragCommitMetric = defineMetric<
  CellRangeSelectionPointerDragCommitProbeData,
  MetricMetadataBase
>({
  metricName:
    MetricName.DATABASE_CELL_RANGE_SELECTION_POINTER_DRAG_COMMIT_INTERACTION_TIME,
  buildProbe: ({ tableRoot, rowIndex, colIndex, entryId, fieldId }) => {
    return () =>
      getRangeHighlightVisibleForTargetCellFrameState(tableRoot, {
        rowIndex,
        colIndex,
        entryId,
        fieldId
      });
  },
  buildMetadata: () => ({
    interactionTimingSource: 'selections-range-highlight-visible'
  })
});
